import { ReactNode, useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputAdornment, OutlinedInput, Stack, Typography } from '@mui/material';
import { AUTH_TOKENS } from './auth-branding';

const INK_TERTIARY = AUTH_TOKENS.inkTertiary;

interface AuthFieldProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  icon: ReactNode;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: string;
  /** Skip the brief error-reveal debounce (e.g. once the user has hit submit). */
  immediate?: boolean;
  focusRing: string;
  disabled?: boolean;
  /** Optional right-aligned action beside the label (e.g. Forgot password). */
  labelAction?: ReactNode;
  /** Optional trailing adornment inside the field (e.g. password show/hide). */
  endAdornment?: ReactNode;
}

/**
 * Labelled text input matching the auth handoff: external 0.8125rem label, 50px
 * field, 11px radius, leading icon, and a green 3px focus ring derived from the
 * active primary. Greens/ring come from the theme so partner branding applies.
 */
export const AuthField = ({
  id,
  name,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  icon,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  immediate,
  focusRing,
  disabled,
  labelAction,
  endAdornment,
}: AuthFieldProps) => {
  // Defer showing the error state, and reset the timer on every value change, so
  // browser autofill / password managers that fill character-by-character (with
  // transiently invalid values, and sometimes a mid-fill blur) don't flash the
  // red border/message before the full value lands. An explicit submit
  // (`immediate`) skips the delay, since a deliberate submit has nothing to
  // debounce and the user expects feedback at once.
  const [visibleError, setVisibleError] = useState<boolean>(false);
  useEffect(() => {
    if (!error) {
      setVisibleError(false);
      return;
    }
    if (immediate) {
      setVisibleError(true);
      return;
    }
    const timer = setTimeout(() => setVisibleError(true), 250);
    return () => clearTimeout(timer);
  }, [error, value, immediate]);

  const helperId = `${id}-helper`;
  const showHelper = Boolean(visibleError && helperText);
  return (
    <FormControl fullWidth error={visibleError}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: '7px' }}
      >
        <Typography
          component="label"
          htmlFor={id}
          sx={{ fontSize: '0.8125rem', fontWeight: 500, color: AUTH_TOKENS.inkSecondary }}
        >
          {label}
        </Typography>
        {labelAction}
      </Stack>
      <OutlinedInput
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete={autoComplete}
        fullWidth
        disabled={disabled}
        aria-describedby={showHelper ? helperId : undefined}
        startAdornment={
          <InputAdornment position="start" sx={{ mr: 0, color: INK_TERTIARY }}>
            {icon}
          </InputAdornment>
        }
        endAdornment={
          endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined
        }
        sx={{
          height: 50,
          borderRadius: '11px',
          backgroundColor: 'common.white',
          fontSize: '0.9375rem',
          color: 'text.primary',
          pl: '14px',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: AUTH_TOKENS.border,
          },
          '& input': {
            pl: '10px',
            '&::placeholder': { color: INK_TERTIARY, opacity: 1 },
          },
          // Leading icon at a fixed 20px per the design.
          '& .MuiSvgIcon-root': { fontSize: 20 },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: visibleError ? 'error.main' : AUTH_TOKENS.border,
          },
          '&.Mui-focused': {
            boxShadow: visibleError ? 'none' : `0 0 0 3px ${focusRing}`,
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: visibleError ? 'error.main' : 'primary.main',
            },
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'error.main',
          },
        }}
      />
      {showHelper && (
        <FormHelperText id={helperId} sx={{ ml: '2px' }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
